import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import StyledInputLabelColorAndItemDescriptionsFont from 'components/StyledInputLabelColorAndItemDescriptionsFont';
import StyledSelectOptionColors from 'components/StyledSelectOptionColors';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: muiPropForwarding('fullWidth')
})(({ theme, fullWidth }) => ({
  ...(fullWidth
    ? { width: '100%', marginBottom: '20px' }
    : {
        width: '168px',
        [theme.breakpoints.down('sm')]: {
          width: '100%'
        }
      })
}));

const StyledInputLabel = styled(StyledInputLabelColorAndItemDescriptionsFont)(
  ({ theme }) => {
    return {
      marginTop: '5px',
      '&.Mui-focused:not(&.Mui-error)': {
        color: theme.palette.secondary.main
      }
    };
  }
);

const StyledSelect = styled(StyledSelectOptionColors)(({ theme }) => ({
  marginTop: '5px',
  '& .MuiNativeSelect-select': {
    '&:focus': {
      boxShadow: `inset 0 -3px ${theme.palette.secondary.main}`,
      transition: 'box-shadow 100ms'
    }
  },
  '& .MuiNativeSelect-outlined': {
    marginTop: '7px',
    '&.Mui-focused': {
      boxShadow: `inset 0 -10px 0 -8px ${theme.palette.secondary.main}`,
      transition: 'box-shadow 100ms'
    }
  }
}));

function GroupSelect({
  label,
  value,
  onChange,
  id,
  disabled,
  fullWidth,
  options
}) {
  /**
   * build the optgroup tags and options for each
   * value
   */
  const buildMultipleOptionsGroups = () => {
    return options.map((optionGroup, index) => {
      const groupName = optionGroup.label;
      const groupValues = optionGroup.values;

      return (
        <optgroup key={index} label={groupName}>
          {groupValues.map((option, index) => (
            <option disabled={option.disabled} key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </optgroup>
      );
    });
  };

  /**
   * Build options without option groups if it only spans one day
   */
  const buildOptions = () => {
    const optionGroup = options[0];
    return optionGroup.values.map((option, index) => (
      <option disabled={option.disabled} key={index} value={option.value}>
        {option.label}
      </option>
    ));
  };

  return (
    <StyledFormControl fullWidth={fullWidth}>
      <StyledInputLabel htmlFor={id}>{label}</StyledInputLabel>
      <StyledSelect
        native
        id={id}
        data-testid={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        variant="outlined"
        input={<OutlinedInput />}
      >
        {options.length === 1 ? buildOptions() : buildMultipleOptionsGroups()}
      </StyledSelect>
    </StyledFormControl>
  );
}

GroupSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.object),
  id: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool
};

export default GroupSelect;
